import React, { useMemo, useRef, useState } from 'react';
import {
  exportJson, exportCsv, exportHtml, exportPdf, exportEcoZip, exportBomZip, INDEX_FORMATS,
} from './exportUtils';
import { ColumnCheckList } from './ColumnPicker';
import { COST_COLUMN_KEYS } from '../utils/cost';
import { getBomUser } from '../settings/settingsStore';
import { startExportJob } from './exportJobs';

const FORMATS = [
  { id: 'json', label: 'JSON' },
  { id: 'csv',  label: 'CSV' },
  { id: 'html', label: 'HTML (interactive)' },
  { id: 'pdf',  label: 'PDF' },
];

const VIEWS = [
  { id: 'hierarchic', label: 'Hierarchic', desc: 'Full tree with level numbers' },
  { id: 'summary',    label: 'Summary',    desc: 'One line per part, total quantity' },
];

// Column selection is remembered per user, so exporting the same subset twice
// doesn't mean re-ticking the list. Stored as column keys; keys that don't
// exist in the current filter are ignored on read, and an empty intersection
// falls back to "everything".
function selectionKey(prefKey = 'bom_export_cols') {
  return `${prefKey}_${getBomUser()}`;
}

function loadSelection(columns, prefKey) {
  const all = new Set(columns.map((c) => c.key));
  try {
    const saved = JSON.parse(localStorage.getItem(selectionKey(prefKey)) || 'null');
    if (Array.isArray(saved)) {
      const kept = saved.filter((k) => all.has(k));
      if (kept.length) return new Set(kept);
    }
  } catch { /* malformed pref — fall through to "all columns" */ }
  return all;
}

function saveSelection(keys, prefKey) {
  try { localStorage.setItem(selectionKey(prefKey), JSON.stringify([...keys])); } catch {}
}

// "With files" exports a zip: an index in the picked format plus the files it links to.
// An ECO always exports that way (the files it released are the point of it). A BOM offers
// it always: the DMS items' approved revisions when the account has a DMS filter, and the
// plain attachments (STEP models, drawings) on every part and assembly regardless.
//
// Product structures use the same window (PsExportModal): `nodes` is their tree, already built
// in the export's node shape, so nothing is walked in Orcanos; `name`, `prefKey` and `note` are theirs.
export default function ExportModal({ node, columns, onClose, treeView = 'boms', nodes, name, prefKey, note }) {
  const ecoMode = treeView === 'ecos' && node?.row?.type === 'CR';
  const [withFiles, setWithFiles] = useState(ecoMode);
  const [format, setFormat] = useState('html');
  const [indexFormat, setIndexFormat] = useState('html');
  const [withAttachments, setWithAttachments] = useState(true);
  const [view, setView] = useState('hierarchic');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const closedRef = useRef(false);
  const [selected, setSelected] = useState(() => loadSelection(columns, prefKey));

  const itemName = name || node?.row?.objName || node?.row?.userPrefix || 'BOM';

  // Preserve the grid's column order, keep only what's ticked.
  const exportColumns = useMemo(
    () => columns.filter((c) => selected.has(c.key)),
    [columns, selected]
  );

  // Summary substitutes its own aggregates for Quantity and the cost columns,
  // so ticking them there changes nothing — say so instead of pretending.
  const summaryIgnored = useMemo(
    () =>
      view === 'summary' &&
      exportColumns.some(
        (c) => c.key === '__quantity' || COST_COLUMN_KEYS.has(c.key)
      ),
    [view, exportColumns]
  );

  function changeSelection(next) {
    setSelected(next);
    saveSelection(next, prefKey);
  }

  // The zip carries files per row, so it is always the hierarchy: switching to it while
  // Summary is picked would otherwise export a view whose rows aren't items.
  function changeWithFiles(on) {
    setWithFiles(on);
    if (on) setView('hierarchic');
  }

  const formats = withFiles ? INDEX_FORMATS : FORMATS;
  const current = withFiles ? indexFormat : format;
  const pickFormat = withFiles ? setIndexFormat : setFormat;

  // Closing the window while an export runs only hides it here: the run goes on in this tab and
  // its progress moves to the page-level chip (exportJobs / BackgroundExports).
  function close() {
    closedRef.current = true;
    onClose();
  }

  async function handleExport() {
    setExporting(true);
    setProgress(null);
    setError('');
    const job = startExportJob(itemName);
    try {
      const opts = {
        bomName: itemName,
        summary: view === 'summary',
        onProgress: (done, total) => { setProgress({ done, total }); job.progress(done, total); },
        view: treeView,
        nodes,
      };
      const cols = exportColumns;
      const rootRow = node?.row;
      if (withFiles && ecoMode) {
        await exportEcoZip(node.row, cols, {
          ...opts,
          indexFormat,
          includeAttachments: withAttachments,
        });
      }
      else if (withFiles) {
        await exportBomZip(rootRow, cols, {
          ...opts,
          indexFormat,
          includeAttachments: withAttachments,
        });
      }
      else if (format === 'json') await exportJson(rootRow, cols, opts);
      else if (format === 'csv') await exportCsv(rootRow, cols, opts);
      else if (format === 'html') await exportHtml(rootRow, cols, opts);
      else if (format === 'pdf') await exportPdf(rootRow, cols, opts);
      job.finish();
      // Closed already: the parent may show another window by now — don't close that one.
      if (!closedRef.current) onClose();
    } catch (err) {
      job.fail(err.message);
      setError(err.message || 'Export failed.');
    } finally {
      setExporting(false);
      setProgress(null);
    }
  }

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Export</h2>
          <button className="modal-close" onClick={close} aria-label="Close">✕</button>
        </div>

        <div className="modal-body">
          <p className="export-modal-bom-name">{itemName}</p>
          {note && <p className="export-note">{note}</p>}
          {exporting && (
            <p className="export-note export-bg-note" role="status">
              You can close this window and keep working — the export goes on, its progress shows at the
              bottom of the page and the file downloads when it is ready. Don't reload the page until then.
            </p>
          )}

          {!ecoMode && (
            <label className="export-check">
              <input
                type="checkbox"
                checked={withFiles}
                onChange={(e) => changeWithFiles(e.target.checked)}
              />
              <span>
                <strong>Export with files</strong>
                <span className="export-option-desc"> — a zip of the index and the files it links to</span>
              </span>
            </label>
          )}

          {!ecoMode && !withFiles && (
          <fieldset className="export-format-group">
            <legend className="export-section-label">View</legend>
            {VIEWS.map((v) => (
              <label
                key={v.id}
                className={`bom-export-option${view === v.id ? ' is-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="export-view"
                  value={v.id}
                  checked={view === v.id}
                  onChange={() => setView(v.id)}
                />
                <span>
                  <strong>{v.label}</strong>
                  <span className="export-option-desc"> — {v.desc}</span>
                </span>
              </label>
            ))}
          </fieldset>
          )}

          <fieldset className="export-format-group">
            <legend className="export-section-label">{withFiles ? 'Index format' : 'Format'}</legend>
            {formats.map((f) => (
              <label
                key={f.id}
                className={`bom-export-option${current === f.id ? ' is-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="export-format"
                  value={f.id}
                  checked={current === f.id}
                  onChange={() => pickFormat(f.id)}
                />
                {f.label}
              </label>
            ))}
            {withFiles && (
              <label className="export-check">
                <input
                  type="checkbox"
                  checked={withAttachments}
                  onChange={(e) => setWithAttachments(e.target.checked)}
                />
                <span>
                  {ecoMode
                    ? "Include the documents' attachments (images, STEP models…)"
                    : 'Include attachments (STEP models, drawings…)'}
                </span>
              </label>
            )}
            {ecoMode && (
              <p className="export-note">
                The zip holds the ECO hierarchy as the index and a files folder with the file
                of each DMS item this ECO released
                {withAttachments && ', plus an attachments folder with the other files attached to each of those documents'}
                , linked from the index. Orcanos records each file as downloaded by you.
              </p>
            )}
            {!ecoMode && withFiles && (
              <p className="export-note">
                The zip holds the hierarchy as the index, a files folder with the latest
                approved revision of every DMS item in it
                {withAttachments && ', and an attachments folder with the files attached to each part, assembly and document'}
                , all linked from the index. Orcanos records each file as downloaded by you.
              </p>
            )}
          </fieldset>

          <fieldset className="export-format-group">
            <legend className="export-section-label">
              Columns
              <span className="export-option-desc">
                {' '}— {selected.size} of {columns.length}
              </span>
            </legend>
            <div className="col-picker-quick">
              <button
                type="button"
                onClick={() => changeSelection(new Set(columns.map((c) => c.key)))}
              >
                All
              </button>
              <button type="button" onClick={() => changeSelection(new Set())}>
                None
              </button>
            </div>
            <ColumnCheckList
              columns={columns}
              selected={selected}
              onChange={changeSelection}
            />
            {summaryIgnored && (
              <p className="export-note">
                Summary reports its own Total Qty, Occurrences and cost totals, so
                the Quantity and cost columns are replaced rather than repeated.
              </p>
            )}
          </fieldset>
        </div>

        <div className="modal-footer">
          {error && !exporting && <span className="export-error" role="alert">{error}</span>}
          {exporting && (
            <span className="export-progress-label">
              {progress
                ? `Fetching… ${progress.done} / ${progress.total}`
                : 'Fetching…'}
            </span>
          )}
          <button className="btn-secondary" onClick={close}>{exporting ? 'Close' : 'Cancel'}</button>
          <button
            className="btn-primary"
            onClick={handleExport}
            disabled={exporting || selected.size === 0}
            title={selected.size === 0 ? 'Pick at least one column' : undefined}
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
}
